import type { WorkflowStep } from '../services/workflowService';
import { validateWorkflowDefinition } from './workflowDefinitionValidation';
import { getIncoherentActionsForStep, wouldActionBeIncoherentForStep } from './workflowActionCoherence';
import {
  canonicalStepType,
  filterAllowedActionsForStepType,
  getApplicableActionsForStepType,
  getStepTypeLabel,
  isApplicableActionForStepType,
  isArchiveStepType,
  isEditStepType,
  isPublishStepType,
  isRequiredStepType,
  isReviewPhaseStepType,
  isSignatureStepType,
  isSingleInstanceStepType,
  WorkflowActionCodes,
  WorkflowStepType,
} from './workflowConstants';

function newClientStepId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0')}`;
}

export function sortStepsByOrder<T extends Pick<WorkflowStep, 'id' | 'order'>>(steps: T[]): T[] {
  return [...steps].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return (a.id || '').localeCompare(b.id || '');
  });
}

export function getDefaultWorkflowSteps(): WorkflowStep[] {
  const mk = (
    order: number,
    name: string,
    type: string,
    allowedActions: string[],
    extras?: Partial<WorkflowStep>
  ): WorkflowStep => ({
    id: newClientStepId(),
    workflowId: '',
    name,
    description: '',
    type,
    order,
    slaId: undefined,
    sendEmailNotification: type !== WorkflowStepType.Archive,
    requiresComment: false,
    approvalRule: 'Any',
    minApprovals: 1,
    allowedActions,
    assignees: [],
    ...extras,
  });

  return [
    mk(1, 'Edição', WorkflowStepType.Edit, ['Save', 'SendForReview']),
    mk(2, 'Revisão', WorkflowStepType.Review, ['Comment', 'Approve', 'Reject']),
    mk(3, 'Publicação', WorkflowStepType.Publish, ['Comment', 'Edit', 'Archive']),
    mk(4, 'Arquivamento', WorkflowStepType.Archive, ['Archive', 'Edit'], { sendEmailNotification: false }),
  ];
}

export function buildNewEditStep(): WorkflowStep {
  return {
    id: newClientStepId(),
    workflowId: '',
    name: '',
    description: '',
    type: WorkflowStepType.Edit,
    order: 1,
    slaId: undefined,
    sendEmailNotification: true,
    requiresComment: false,
    approvalRule: 'Any',
    minApprovals: 1,
    allowedActions: ['Save', 'SendForReview'],
    assignees: [],
  };
}

export function insertNewEditStep<T extends WorkflowStep>(steps: T[]): T[] {
  const sorted = sortStepsByOrder(steps);
  const toInsert = buildNewEditStep() as unknown as T;
  const next = [toInsert, ...sorted];
  return next.map((s, i) => ({ ...s, order: i + 1 }));
}

export function reorderStepsPreserving<T extends WorkflowStep>(steps: T[], sourceIndex: number, destIndex: number): T[] {
  const sorted = sortStepsByOrder(steps);
  const items = [...sorted];
  const [moved] = items.splice(sourceIndex, 1);
  items.splice(destIndex, 0, moved);
  return items.map((s, i) => ({ ...s, order: i + 1 }));
}

/**
 * Motivo pelo qual a etapa NÃO pode ser removida, ou `null` se a remoção é permitida.
 * A obrigatoriedade é explícita e baseada apenas no TIPO/presença da etapa — não na
 * coerência das ações das demais etapas (essas são ajustadas automaticamente na remoção).
 */
export function getStepDeletionBlockReason(steps: WorkflowStep[], stepId: string): string | null {
  const step = steps.find(s => s.id === stepId);
  if (!step) {
    return 'Etapa não encontrada.';
  }
  if (!isRequiredStepType(step.type)) {
    return null;
  }
  const canonical = canonicalStepType(step.type);
  const sameTypeCount = steps.filter(s => canonicalStepType(s.type) === canonical).length;
  if (sameTypeCount <= 1) {
    return `A etapa de ${getStepTypeLabel(step.type)} é obrigatória e não pode ser removida.`;
  }
  return null;
}

export function canDeleteWorkflowStep(steps: WorkflowStep[], stepId: string): boolean {
  return getStepDeletionBlockReason(steps, stepId) === null;
}

/**
 * Motivo pelo qual a etapa NÃO pode ter o tipo alterado para `newType`, ou `null` se permitido.
 * Cobre etapas obrigatórias (ex.: única Edição) e unicidade (Publicação, Assinatura, Arquivamento).
 */
export function getStepTypeChangeBlockReason(
  steps: WorkflowStep[],
  stepId: string,
  newType: string
): string | null {
  const sorted = sortStepsByOrder(steps);
  const step = sorted.find(s => s.id === stepId);
  if (!step) {
    return 'Etapa não encontrada.';
  }
  if (isSameTypeGroup(step.type, newType)) {
    return null;
  }

  if (isSingleInstanceStepType(newType) && sorted.some(s => s.id !== stepId && isSameTypeGroup(s.type, newType))) {
    return `O workflow pode ter no máximo uma etapa de ${getStepTypeLabel(newType)}.`;
  }

  if (isRequiredStepType(step.type) && !sorted.some(s => s.id !== stepId && isSameTypeGroup(s.type, step.type))) {
    return `A etapa de ${getStepTypeLabel(step.type)} é obrigatória e não pode ser convertida.`;
  }

  return null;
}

/**
 * Remove das etapas restantes as ações de transição que ficaram incoerentes após uma
 * alteração no fluxo (ex.: `SendForReview` na Edição quando não há mais etapa de Revisão).
 * Evita que uma etapa opcional pareça "obrigatória" só porque sua remoção deixaria
 * ações penduradas em outras etapas.
 */
export function sanitizeWorkflowStepActions<T extends WorkflowStep>(steps: T[]): T[] {
  const ordered = sortStepsByOrder(steps).map((s, i) => ({ ...s, order: i + 1 }));
  return ordered.map((step, index) => {
    const incoherent = getIncoherentActionsForStep(step, index, ordered);
    if (incoherent.length === 0) {
      return step;
    }
    const toRemove = new Set(incoherent.map(i => i.action.trim().toLowerCase()));
    return {
      ...step,
      allowedActions: (step.allowedActions ?? []).filter(
        a => !!a?.trim() && !toRemove.has(a.trim().toLowerCase())
      ),
    };
  });
}

export function canMoveStepToIndex(steps: WorkflowStep[], sourceIndex: number, destIndex: number): boolean {
  if (sourceIndex === destIndex) return true;
  const reordered = reorderStepsPreserving(steps, sourceIndex, destIndex);
  return validateWorkflowDefinition(reordered).length === 0;
}

export function stepCanMoveSomewhere(steps: WorkflowStep[], sortedIndex: number): boolean {
  const sorted = sortStepsByOrder(steps);
  if (sortedIndex < 0 || sortedIndex >= sorted.length) return false;
  for (let d = 0; d < sorted.length; d++) {
    if (d !== sortedIndex && canMoveStepToIndex(sorted, sortedIndex, d)) return true;
  }
  return false;
}

export function placeSignatureAdjacentToPublish<T extends WorkflowStep>(steps: T[], stepId: string): T[] {
  const sorted = sortStepsByOrder(steps);
  const step = sorted.find(s => s.id === stepId);
  if (!step) return sorted;
  const others = sorted.filter(s => s.id !== stepId);
  const pubIdx = others.findIndex(s => isPublishStepType(s.type));
  if (pubIdx < 0) return sorted.map((s, i) => ({ ...s, order: i + 1 }));
  const withSig: T[] = [
    ...others.slice(0, pubIdx),
    {
      ...step,
      type: WorkflowStepType.Signature,
      allowedActions: filterAllowedActionsForStepType(WorkflowStepType.Signature, step.allowedActions),
    } as T,
    ...others.slice(pubIdx),
  ];
  return withSig.map((s, i) => ({ ...s, order: i + 1 }));
}

export function placeArchiveAsLastStep<T extends WorkflowStep>(steps: T[], stepId: string): T[] {
  const sorted = sortStepsByOrder(steps);
  const step = sorted.find(s => s.id === stepId);
  if (!step) return sorted;
  const others = sorted.filter(s => s.id !== stepId);
  const withArchive: T[] = [
    ...others,
    {
      ...step,
      type: WorkflowStepType.Archive,
      allowedActions: filterAllowedActionsForStepType(WorkflowStepType.Archive, step.allowedActions),
    } as T,
  ];
  return withArchive.map((s, i) => ({ ...s, order: i + 1 }));
}

export function placePublishBeforeArchive<T extends WorkflowStep>(steps: T[], stepId: string): T[] {
  const sorted = sortStepsByOrder(steps);
  const step = sorted.find(s => s.id === stepId);
  if (!step) return sorted;
  const others = sorted.filter(s => s.id !== stepId);
  const archIdx = others.findIndex(s => isArchiveStepType(s.type));
  const insertAt = archIdx >= 0 ? archIdx : others.length;
  const withPub: T[] = [
    ...others.slice(0, insertAt),
    {
      ...step,
      type: WorkflowStepType.Publish,
      allowedActions: filterAllowedActionsForStepType(WorkflowStepType.Publish, step.allowedActions),
    } as T,
    ...others.slice(insertAt),
  ];
  return withPub.map((s, i) => ({ ...s, order: i + 1 }));
}

function isSameTypeGroup(a: string, b: string): boolean {
  const canonicalA = canonicalStepType(a);
  return canonicalA !== null && canonicalA === canonicalStepType(b);
}

/** Ações padrão coerentes para cada tipo de etapa (limpas depois por sanitizeWorkflowStepActions). */
export function getDefaultActionsForStepType(type: string): string[] {
  if (isEditStepType(type)) return ['Save', 'SendForReview'];
  if (isReviewPhaseStepType(type)) return ['Comment', 'Approve', 'Reject'];
  if (isSignatureStepType(type)) return ['Sign', 'Reject'];
  if (isPublishStepType(type)) return ['Comment', 'Edit', 'Archive'];
  if (isArchiveStepType(type)) return ['Archive', 'Edit'];
  return [];
}

export function stepAllowsComment(step: Pick<WorkflowStep, 'allowedActions'>): boolean {
  return (step.allowedActions ?? []).some(
    a => (a || '').trim().toLowerCase() === WorkflowActionCodes.Comment.toLowerCase()
  );
}

export function commentApplicableForStepType(stepType: string | null | undefined): boolean {
  return isApplicableActionForStepType(stepType, WorkflowActionCodes.Comment);
}

/** Ações automáticas da etapa (catálogo do tipo, menos Comment, filtradas por coerência do fluxo). */
export function getAutomaticActionsForStep(steps: WorkflowStep[], stepId: string): string[] {
  const step = steps.find(s => s.id === stepId);
  if (!step) {
    return [];
  }

  return getApplicableActionsForStepType(step.type)
    .filter(a => a.toLowerCase() !== WorkflowActionCodes.Comment.toLowerCase())
    .filter(a => !wouldActionBeIncoherentForStep(steps, stepId, a));
}

export function buildAllowedActionsForStep(
  steps: WorkflowStep[],
  stepId: string,
  includeComment: boolean
): string[] {
  const step = steps.find(s => s.id === stepId);
  const automatic = getAutomaticActionsForStep(steps, stepId);
  if (
    includeComment
    && step
    && commentApplicableForStepType(step.type)
  ) {
    return [...automatic, WorkflowActionCodes.Comment];
  }
  return automatic;
}

export function syncStepAllowedActionsFromPolicy<T extends WorkflowStep>(
  steps: T[],
  stepId: string,
  preserveComment: boolean
): T[] {
  return steps.map(s => {
    if (s.id !== stepId) {
      return s;
    }
    return {
      ...s,
      allowedActions: buildAllowedActionsForStep(steps, stepId, preserveComment),
    };
  });
}

export function syncAllStepsAllowedActionsFromPolicy<T extends WorkflowStep>(steps: T[]): T[] {
  const ordered = sortStepsByOrder(steps);
  return ordered.map(step => ({
    ...step,
    allowedActions: buildAllowedActionsForStep(
      ordered,
      step.id,
      stepAllowsComment(step)
    ),
  }));
}

function phaseRank(type: string): number {
  if (isEditStepType(type)) return 0;
  if (isReviewPhaseStepType(type)) return 1;
  if (isSignatureStepType(type)) return 2;
  if (isPublishStepType(type)) return 3;
  if (isArchiveStepType(type)) return 4;
  return 5;
}

/**
 * Reordena as etapas pela fase do ciclo (edição → revisão → assinatura → publicação → arquivamento),
 * preservando a ordem relativa dentro de cada fase. Garante as regras de posicionamento
 * (edições contíguas no início, assinatura imediatamente antes da publicação, arquivamento por último).
 */
export function reorderByPhase<T extends WorkflowStep>(steps: T[]): T[] {
  const sorted = sortStepsByOrder(steps);
  return sorted
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const rank = phaseRank(a.s.type) - phaseRank(b.s.type);
      return rank !== 0 ? rank : a.i - b.i;
    })
    .map(({ s }, i) => ({ ...s, order: i + 1 }));
}

/**
 * Altera o tipo de uma etapa, aplicando ações padrão coerentes, reposicionando o fluxo e
 * limpando ações incoerentes. Retorna um erro quando a alteração violaria as regras
 * (unicidade de Publicação/Assinatura/Arquivamento ou remoção de uma etapa obrigatória).
 */
export function changeStepType<T extends WorkflowStep>(
  steps: T[],
  stepId: string,
  newType: string
): { steps: T[]; error?: string } {
  const sorted = sortStepsByOrder(steps);
  const step = sorted.find(s => s.id === stepId);
  if (!step) {
    return { steps: sorted, error: 'Etapa não encontrada.' };
  }
  if (isSameTypeGroup(step.type, newType)) {
    return { steps: sorted };
  }

  const blockReason = getStepTypeChangeBlockReason(sorted, stepId, newType);
  if (blockReason) {
    return { steps: sorted, error: blockReason };
  }

  const updated = sorted.map(s =>
    s.id === stepId ? { ...s, type: newType, allowedActions: getDefaultActionsForStepType(newType) } : s
  );

  return { steps: sanitizeWorkflowStepActions(reorderByPhase(updated)) };
}
